import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from './RoleCapabilityService';
import RbacPageShell from './RbacPageShell';

const RoleCapabilitiesPage = () => {
    const [roles, setRoles] = useState<api.RoleRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState<'ALL' | 'INTERNAL' | 'EXTERNAL'>('ALL');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.listRoles(false);
            setRoles(res.data.roles || []);
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return roles.filter((r) => {
            if (typeFilter !== 'ALL' && r.type !== typeFilter) return false;
            if (!q) return true;
            return r.name.toLowerCase().includes(q) || r.roleCode.toLowerCase().includes(q);
        });
    }, [roles, filter, typeFilter]);

    return (
        <RbacPageShell
            title="Role → capability mapping"
            subtitle="Select a role to grant or revoke its capabilities."
        >
            {error ? (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            ) : null}

            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 filterSearch gap-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="font-14 me-2">Found {filtered.length} roles</span>
                    <select
                        className="form-select"
                        style={{ width: 160 }}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value as 'ALL' | 'INTERNAL' | 'EXTERNAL')}
                    >
                        <option value="ALL">All types</option>
                        <option value="EXTERNAL">EXTERNAL</option>
                        <option value="INTERNAL">INTERNAL</option>
                    </select>
                </div>
                <div style={{ minWidth: 240, maxWidth: 360 }} className="flex-grow-1 flex-md-grow-0">
                    <input
                        className="form-control"
                        placeholder="Search roles…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <p className="mb-0">Loading…</p>
            ) : (
                <div className="table-responsive attendee_dtl">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Code</th>
                                <th>Type</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((role) => (
                                <tr key={role.id}>
                                    <td>
                                        {role.name}
                                        {role.isSystem ? ' *' : ''}
                                    </td>
                                    <td>{role.roleCode}</td>
                                    <td>{role.type}</td>
                                    <td>
                                        <Link
                                            to={`/support/role-capabilities/${role.id}`}
                                            className="btn btn-green btn-sm"
                                            aria-label={`Open mapping for ${role.name}`}
                                        >
                                            <span>Open Mapping</span>
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length ? (
                                <tr>
                                    <td colSpan={4} className="text-muted">
                                        No roles match the filters.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            )}
        </RbacPageShell>
    );
};

export default RoleCapabilitiesPage;
